import { BehaviorSubject, Observable } from 'rxjs';
import { ScanResult } from '@capacitor-community/bluetooth-le';
import { Capacitor } from '@capacitor/core';
import { IFrequency } from './IFrequency';
import { ISessionLog } from './ISessionLog';
import { IDebugMessage } from './IDebugMessage';
import { PreloadLevelType } from './PreloadLevelType';
import { ITreatment } from './ITreatment';
import { IBleClient } from './IBleClient';
import { BodyPart } from 'src/app/controls/anatomy/anatomy.component';

export class IQConnect {
  private static connected: boolean = false;

  private readonly IMPULSE_INFO_SERVICE_UUID = 'a1e4f01d-0509-4f7a-9578-a18621ea6051';
  private readonly IMPULSE_INFO_READ = 'a1e40102-0509-4f7a-9578-a18621ea6051';
  private readonly IMPULSE_INFO_WRITE = 'a1e40101-0509-4f7a-9578-a18621ea6051';

  private isConnected$ = new BehaviorSubject<boolean>(IQConnect.connected);

  private deviceId: string | null = null;
  private deviceName: string | null = null;
  private serialNumber: string | null = null;
  private currentTreatment: Treatment | null = null;
  private legacyProtocol = true;
  private protocolDetected = false;
  private static readonly PROTOCOL_SUFFIX = [0x00, 0x00, 0x01] as const;
  private static readonly PROTOCOL_PROBE_TIMEOUT_MS = 1000;
  private pendingResponseWaiters = new Map<
    number,
    { resolve: (value: boolean) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private impulseNotificationsEnabled = false;

  public static CMD_TRIGGER_RELEASED = 0x10;
  public static CMD_FREQUENCY = 0x11;
  public static CMD_PIC_SETTING = 0x12;
  public static CMD_DOUBLE_TRIGGER = 0x14;
  public static CMD_ADJUST_SUCCESS = 0xa9;
  public static CMD_PRELOAD = 0x17;
  public static CMD_SESSION_LOG = 0x02;
  public static CMD_DEVICE_MANUFACTURER = 0xf0;

  public static REQ_SESSION_LOG = 0x02;
  public static REQ_PIC_SETTINGS = 0x12;

  public static REQ_WRITE_MANUFACTURER = 0xe200;
  /** Write display board serial (TX 0xE3 + 9-byte DisplayBrdSN). */
  public static WRITE_DISPLAY_BRD_SN = 0xe3;
  /** Query display board serial (TX 0xF3); response 0xF3 + 9-byte DisplayBrdSN. */
  public static REQ_DISPLAY_BRD_SN = 0xf3;
  /** Write main board serial (TX 0xE4 + 9-byte MainBrdSN). */
  public static WRITE_MAIN_BRD_SN = 0xe4;
  /** Query main board serial (TX 0xF4); response 0xF4 + 9-byte MainBrdSN. */
  public static REQ_MAIN_BRD_SN = 0xf4;
  /** Query display and main board firmware versions (TX 0xF6); response 0xF6 + 4-byte fw_ver_packet. */
  public static REQ_BOARD_FIRMWARE_VERSION = 0xF6;
  static readonly FACTORY_SERIAL_FIELD_LENGTH = 9;
  /** BleCmd_DeviceChange (0x0001) — write to set instrument device type */
  public static REQ_DEVICE_CHANGE = 0x0001;
  /** Full TX packet to configure instrument as Connect after firmware update */
  public static readonly CONNECT_INSTRUMENT_PACKET = new Uint8Array([
    0x00, 0x01, 0x3f, 0x74, 0x58, 0x4d, 0x04,
  ]);

  private static readonly LEGACY_FORCE_VALUE_BY_LEVEL: Readonly<Record<number, number>> = {
    1: 25,
    2: 50,
    3: 100,
    4: 200,
    5: 300,
    6: 400,
  };

  private static readonly MODERN_FORCE_VALUE_BY_LEVEL: Readonly<Record<number, number>> = {
    1: 50,
    2: 75,
    3: 100,
    4: 200,
    5: 300,
    6: 400,
  };

  private sessionLogInterval: any;

  // Correctly initialize BehaviorSubjects

  private force$ = new BehaviorSubject<number | null>(null);
  private forceIndex: number | null = null;

  private preload$ = new BehaviorSubject<PreloadLevelType>(PreloadLevelType.none);
  private triggerState$ = new BehaviorSubject<boolean>(false);
  private onDoubleTrigger$ = new BehaviorSubject<void>(undefined);

  private connected$ = new BehaviorSubject<string | null>(null);
  private disconnected$ = new BehaviorSubject<void>(undefined);

  private serialNumber$ = new BehaviorSubject<string | null>(null);
  private displayBoardSn$ = new BehaviorSubject<string | null>(null);
  private mainBoardSn$ = new BehaviorSubject<string | null>(null);
  private displayBoardFw$ = new BehaviorSubject<string | null>(null);
  private mainBoardFw$ = new BehaviorSubject<string | null>(null);
  private initialized$ = new BehaviorSubject<boolean>(false);

  private onScanning$ = new BehaviorSubject<boolean>(false);
  private debug$ = new BehaviorSubject<IDebugMessage | null>(null);
  private error$ = new BehaviorSubject<IDebugMessage | null>(null);

  private onSessionLog$ = new BehaviorSubject<ISessionLog | null>(null);

  private treatment$ = new BehaviorSubject<Treatment | null>(null);
  private frequency$ = new BehaviorSubject<IFrequency[] | null>(null);

  protected bleClient: IBleClient;

  constructor(bleClient: IBleClient) {
    this.bleClient = bleClient;
  }

  async initializeBLE() {
    this.isConnected$.next(IQConnect.connected);

    if (!IQConnect.connected) {
      try {
        await this.bleClient.initialize();
      } catch (error) {
        if (this.isBleUnavailableError(error)) {
          this.isConnected$.next(false);
          this.initialized$.next(false);
          this.log('BLE unavailable:', error instanceof Error ? error.message : error);
          return;
        }
        throw error;
      }
    } else {
      await this.enableNotifications();
      if (!this.protocolDetected) {
        await this.detectCommandProtocol();
      }
      this.setSerialNumberFromDeviceName();
      this.initialized$.next(true);
      this.triggerState$.next(false);
      await this.requestPicSettings();
    }
  }

  public async startScanning(deviceId: string | null) {
    await this.scan();
  }

  private async scan() {
    try {
      // Stop any ongoing scan first (for native platforms)
      try {
        await this.bleClient.stopLEScan();
      } catch (e) {
        // Ignore errors if scan wasn't running
      }

      this.onScanning$.next(true);
      // Show all BLE devices - user can select any device
      // requestDevice works on both web and native platforms via Capacitor
      const device = await this.bleClient.requestDevice({
        services: [this.IMPULSE_INFO_SERVICE_UUID],
        allowDuplicates: false,
      });
      this.onScanning$.next(false);

      await this.connect(device.deviceId, device.name ?? null);
    } catch (error) {
      this.logError('Scan failed:', error);
      this.onScanning$.next(false);
    }
  }

  async stopScanning() {
    await this.bleClient.stopLEScan();
  }

  private async connect(deviceId: string, deviceName: string | null = null) {
    try {
      await this.bleClient.connect(deviceId, () => {
        this.isConnected$.next(false);
        this.deviceId = null;
        this.disconnected$.next();

        if (this.sessionLogInterval) {
          clearInterval(this.sessionLogInterval);
        }
      });
      this.deviceId = deviceId;
      this.deviceName = deviceName;
      IQConnect.connected = true;

      this.isConnected$.next(IQConnect.connected);
      this.connected$.next(deviceId);
      await this.enableNotifications();
      await this.detectCommandProtocol();
      this.setSerialNumberFromDeviceName();
      this.initialized$.next(true);
      this.triggerState$.next(false);
      await this.requestPicSettings();

      this.sessionLogInterval = setInterval(() => {
        this.requestSessionLog();
      }, 5000);
    } catch (error) {
      this.deviceId = null;
      this.isConnected$.next(false);
      this.disconnected$.next();
      this.initialized$.next(false);
    }
  }

  async disconnect(): Promise<void> {
    if (!this.deviceId) return;
    const id = this.deviceId;
    if (this.sessionLogInterval) {
      clearInterval(this.sessionLogInterval);
      this.sessionLogInterval = null;
    }
    try {
      await this.bleClient.disconnect(id);
    } finally {
      this.deviceId = null;
      this.deviceName = null;
      this.legacyProtocol = true;
      this.protocolDetected = false;
      this.impulseNotificationsEnabled = false;
      this.clearPendingResponseWaiters();
      IQConnect.connected = false;
      this.isConnected$.next(false);
      this.serialNumber = null;
      this.serialNumber$.next(null);
      this.displayBoardSn$.next(null);
      this.mainBoardSn$.next(null);
      this.displayBoardFw$.next(null);
      this.mainBoardFw$.next(null);
      this.disconnected$.next();
    }
  }

  private async enableNotifications() {
    if (this.impulseNotificationsEnabled) {
      return;
    }

    try {
      await this.bleClient.startNotifications(
        this.deviceId!,
        this.IMPULSE_INFO_SERVICE_UUID,
        this.IMPULSE_INFO_READ,
        async (value) => {
          // Fix: Convert DataView to Uint8Array correctly
          const receivedData = new Uint8Array(value.buffer);
          await this.processImpulseCommand(receivedData);
        },
      );
      this.impulseNotificationsEnabled = true;
    } catch (error) {
      this.logError('Failed to enable notifications:', error);
    }
  }

  private async writeToImpulse(data: Uint8Array, src: string) {
    this.log('TX:', src, this.dataToString(data));
    try {
      // Fix: Convert Uint8Array to DataView
      const dataView = new DataView(data.buffer);
      await this.bleClient.write(
        this.deviceId!,
        this.IMPULSE_INFO_SERVICE_UUID,
        this.IMPULSE_INFO_WRITE,
        dataView,
      );
    } catch (error) {
      this.logError('Write failed:' + src);
    }
  }

  private buildProtocolPacket(commandBytes: number[]): Uint8Array {
    if (this.legacyProtocol) {
      return new Uint8Array(commandBytes);
    }
    return new Uint8Array([...commandBytes, ...IQConnect.PROTOCOL_SUFFIX]);
  }

  private async sendProtocolCommand(commandBytes: number[], src: string): Promise<void> {
    await this.writeToImpulse(this.buildProtocolPacket(commandBytes), src);
  }

  private waitForCommandResponse(expectedCommand: number, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pendingResponseWaiters.delete(expectedCommand);
        resolve(false);
      }, timeoutMs);

      this.pendingResponseWaiters.set(expectedCommand, { resolve, timer });
    });
  }

  private notifyCommandResponse(command: number): void {
    const waiter = this.pendingResponseWaiters.get(command);
    if (!waiter) {
      return;
    }

    clearTimeout(waiter.timer);
    this.pendingResponseWaiters.delete(command);
    waiter.resolve(true);
  }

  private clearPendingResponseWaiters(): void {
    for (const waiter of this.pendingResponseWaiters.values()) {
      clearTimeout(waiter.timer);
    }
    this.pendingResponseWaiters.clear();
  }

  private async detectCommandProtocol(): Promise<void> {
    this.clearPendingResponseWaiters();
    this.displayBoardFw$.next(null);
    this.mainBoardFw$.next(null);

    const cmd = IQConnect.REQ_BOARD_FIRMWARE_VERSION;
    const timeout = IQConnect.PROTOCOL_PROBE_TIMEOUT_MS;

    const legacyWait = this.waitForCommandResponse(cmd, timeout);
    await this.writeToImpulse(new Uint8Array([cmd]), 'detectProtocol-legacy');
    if (await legacyWait) {
      this.legacyProtocol = true;
      this.protocolDetected = true;
      this.log('Protocol detected: legacy');
      return;
    }

    const modernWait = this.waitForCommandResponse(cmd, timeout);
    await this.writeToImpulse(
      new Uint8Array([cmd, ...IQConnect.PROTOCOL_SUFFIX]),
      'detectProtocol-modern',
    );
    if (await modernWait) {
      this.legacyProtocol = false;
      this.protocolDetected = true;
      this.log('Protocol detected: modern');
      return;
    }

    this.logError('Protocol detection failed, defaulting to legacy');
    this.protocolDetected = true;
  }

  private setSerialNumberFromDeviceName(): void {
    const serial = this.deviceName?.trim() || null;
    this.serialNumber = serial;
    this.serialNumber$.next(serial);
  }

  /** Sends 0xF3 then 0xF4 to query display and main board serial numbers from firmware. */
  public async requestDisplayAndMainBoardSerialNumbers() {
    this.displayBoardSn$.next(null);
    this.mainBoardSn$.next(null);

    await this.sendProtocolCommand([IQConnect.REQ_DISPLAY_BRD_SN], 'requestDisplayBrdSN');

    await this.sendProtocolCommand([IQConnect.REQ_MAIN_BRD_SN], 'requestMainBrdSN');
  }

  /** Sends 0xF6 to query display and main board firmware versions. */
  public async requestBoardFirmwareVersions(): Promise<void> {
    this.displayBoardFw$.next(null);
    this.mainBoardFw$.next(null);
    await this.sendProtocolCommand(
      [IQConnect.REQ_BOARD_FIRMWARE_VERSION],
      'requestBoardFirmwareVersions',
    );
  }

  /** Sends BleCmd_DeviceChange (0x0001) with Connect instrument encrypted payload. */
  public async writeConnectInstrumentType(): Promise<void> {
    await this.writeToImpulse(
      new Uint8Array(IQConnect.CONNECT_INSTRUMENT_PACKET),
      'writeConnectInstrument',
    );
  }
  /**
   * Get command name from command byte value
   */
  private getCommandName(command: number): string {
    switch (command) {
      case IQConnect.CMD_TRIGGER_RELEASED:
        return 'CMD_TRIGGER_RELEASED';
      case IQConnect.CMD_FREQUENCY:
        return 'CMD_FREQUENCY';
      case IQConnect.CMD_PIC_SETTING:
        return 'CMD_PIC_SETTING';
      case IQConnect.CMD_DOUBLE_TRIGGER:
        return 'CMD_DOUBLE_TRIGGER';
      case IQConnect.CMD_ADJUST_SUCCESS:
        return 'CMD_ADJUST_SUCCESS';
      case IQConnect.CMD_PRELOAD:
        return 'CMD_PRELOAD';
      case IQConnect.REQ_DISPLAY_BRD_SN:
        return 'REQ_DISPLAY_BRD_SN';
      case IQConnect.REQ_MAIN_BRD_SN:
        return 'REQ_MAIN_BRD_SN';
      case IQConnect.REQ_BOARD_FIRMWARE_VERSION:
        return 'REQ_BOARD_FIRMWARE_VERSION';
      case IQConnect.CMD_SESSION_LOG:
        return 'CMD_SESSION_LOG';
      default:
        return `UNKNOWN_CMD_0x${command.toString(16).toUpperCase().padStart(2, '0')}`;
    }
  }

  /**
   * Process received BLE commands and trigger corresponding callback events
   */
  private async processImpulseCommand(data: Uint8Array) {
    const command = data[0];
    this.notifyCommandResponse(command);
    const commandName = this.getCommandName(command);
    const hexData = this.dataToString(data);

    this.log('RX:', commandName, hexData);

    switch (command) {
      case IQConnect.CMD_TRIGGER_RELEASED:
        await this.triggerReleased(data);
        break;
      case IQConnect.CMD_FREQUENCY:
        await this.frequency(data);
        break;
      case IQConnect.CMD_PIC_SETTING:
        await this.picSetting(data);
        break;
      case IQConnect.CMD_DOUBLE_TRIGGER:
        await this.doubleTrigger();
        break;
      case IQConnect.CMD_ADJUST_SUCCESS:
        await this.adjustSuccess(data);
        break;
      case IQConnect.CMD_PRELOAD:
        await this.processPreload(data);
        break;
      case IQConnect.REQ_DISPLAY_BRD_SN:
        await this.processDisplayBoardSn(data);
        break;
      case IQConnect.REQ_MAIN_BRD_SN:
        await this.processMainBoardSn(data);
        break;
      case IQConnect.REQ_BOARD_FIRMWARE_VERSION:
        await this.processBoardFirmwareVersions(data);
        break;
      case IQConnect.CMD_SESSION_LOG:
        await this.sessionLog(data);
        break;
      default:
        this.log('Unknown Command:', commandName, hexData);
    }
  }

  private dataToString(data: Uint8Array): string {
    return Array.from(data)
      .map((byte) => `${byte.toString(16).toUpperCase().padStart(2, '0')}`)
      .join('');
  }

  private getUIntAt(data: Uint8Array, idx: number): number {
    return (
      (((data[idx + 3] & 0xff) << 24) |
        ((data[idx + 2] & 0xff) << 16) |
        ((data[idx + 1] & 0xff) << 8) |
        (data[idx] & 0xff)) >>>
      0
    ); // Ensure unsigned value
  }

  private getUShortAt(data: Uint8Array, idx: number): number {
    return (((data[idx + 1] & 0xff) << 8) | (data[idx] & 0xff)) >>> 0; // Ensure unsigned value
  }

  private async sessionLog(data: Uint8Array) {
    /*
        uint16_t patients;
        uint16_t triggers;  //This is 0 based, add 1
        uint16_t dose;
        uint16_t thrusts;
        int16_t SolenoidTemperature;

        
        02 00 00 00 00 00 00 00 00 F7 01

        [02][00][00][00][00][00][00][00][00][D9][FF]
         02  00  00  0D  00  16  00  1C  03  01  00
        */
    if (data.length < 11) {
      this.logError('sessionLog', 'Invalid message received');
    }

    //02 0000 0000 2600 2600 D9FF

    // Extract values from byte array
    // const patients = this.getUShortAt(data, 1); // 1,2
    const triggers = this.getUShortAt(data, 3) + 1; //3,4
    const dose = this.getUShortAt(data, 5); //5,6
    const thrusts = this.getUShortAt(data, 7); //7,8
    const solenoidTemp = 0.48828125 * (this.getUShortAt(data, 9) - 50); //9,10

    this.onSessionLog$.next({
      triggers: triggers,
      thrusts: thrusts,
      solenoidTemp: solenoidTemp,
    });

    if (this.currentTreatment) {
      this.currentTreatment.setReadingData(data);
      this.currentTreatment.setDose(dose);
      if (this.currentTreatment.ended) {
        this.treatment$.next(this.currentTreatment);
        this.currentTreatment = null;
      }
    }
  }

  private async adjustSuccess(data: Uint8Array) {
    // Emit adjustment success event
    if (this.currentTreatment) {
      this.currentTreatment.success = true;
    }
  }

  private async picSetting(data: Uint8Array) {
    if (data.length < 10) {
      this.logError('picSetting', 'Invalid message received');
    }

    const forceIndex = data[5];

    const forceValue = forceIndex + 1;
    this.forceIndex = forceValue;
    this.force$.next(forceValue);

    if (this.currentTreatment) {
      this.currentTreatment.setForce(this.forceIndex, this.usesModernForceMapping());
    }
  }

  private async frequency(data: Uint8Array) {
    if (data.length < 10) {
      this.logError('Frequency', 'Invalid message received');
    }

    const now = new Date();
    const frequencyIndex = data[3];

    // Emit frequency value to subscribers
    if (!this.currentTreatment) {
      this.currentTreatment = new Treatment(now, this.serialNumber!, this.deviceId!);
    }

    const frequencies = this.currentTreatment.addFrequency(now, frequencyIndex);
    if (this.forceIndex) {
      this.currentTreatment.setForce(this.forceIndex, this.usesModernForceMapping());
    }

    this.triggerState$.next(true);
    this.frequency$.next(frequencies);
  }

  private async doubleTrigger() {
    this.onDoubleTrigger$.next();
  }

  private async triggerReleased(data: Uint8Array) {
    this.triggerState$.next(false); //trigger released
    if (this.currentTreatment) {
      this.currentTreatment.ended = new Date();
      this.requestPicSettings();
    }
    await this.requestSessionLog();
  }

  private async requestSessionLog() {
    await this.sendProtocolCommand([IQConnect.REQ_SESSION_LOG], 'requestSessionLog');
  }

  private async requestPicSettings() {
    await this.sendProtocolCommand([IQConnect.REQ_PIC_SETTINGS], 'requestPicSettings');
  }

  private decodeAsciiPayloadAfterCommand(data: Uint8Array): string | null {
    return this.decodeFactorySerialField(data);
  }

  /** Firmware factory fields are uint8[9] ASCII serial numbers after the command byte. */
  private decodeFactorySerialField(data: Uint8Array): string | null {
    if (data.length < 2) {
      return null;
    }

    const payload =
      data.length >= 1 + IQConnect.FACTORY_SERIAL_FIELD_LENGTH
        ? data.slice(1, 1 + IQConnect.FACTORY_SERIAL_FIELD_LENGTH)
        : data.slice(1);

    const text = new TextDecoder()
      .decode(payload)
      .replace(/\0/g, '')
      .trim();

    return text.length > 0 ? text : null;
  }

  private async processDisplayBoardSn(data: Uint8Array) {
    this.displayBoardSn$.next(this.decodeAsciiPayloadAfterCommand(data));
  }

  private async processMainBoardSn(data: Uint8Array) {
    this.mainBoardSn$.next(this.decodeAsciiPayloadAfterCommand(data));
  }

  private formatCentiVersion(lowByte: number, highByte: number): string {
    const centi = (lowByte & 0xff) | ((highByte & 0xff) << 8);
    return (centi / 100).toFixed(2);
  }

  private async processBoardFirmwareVersions(data: Uint8Array) {
    if (data.length < 5) {
      this.logError('processBoardFirmwareVersions', 'Invalid message received');
      return;
    }

    this.displayBoardFw$.next(this.formatCentiVersion(data[1], data[2]));
    this.mainBoardFw$.next(this.formatCentiVersion(data[3], data[4]));
  }

  public async writeSerialNumber(serialNumber: string) {
    if (serialNumber.length > 9) throw Error('Invalid serial number - max length 11');

    serialNumber = serialNumber.padStart(9, '0');

    let command: Uint8Array = new Uint8Array([
      (IQConnect.REQ_WRITE_MANUFACTURER >> 8) & 0xff, // High byte (0xE0)
      IQConnect.REQ_WRITE_MANUFACTURER & 0xff, // Low byte (0x02)
    ]);

    // Convert the serial number string to ASCII bytes
    let serialBytes = new TextEncoder().encode(serialNumber);

    // Create a new Uint8Array with command + serialBytes
    let buff = new Uint8Array(command.length + serialBytes.length + 2);
    buff.set(command, 0); // Set command bytes at the beginning
    buff.set(new Uint8Array([0x00]), command.length + 1);
    buff.set(serialBytes, command.length); // Append serial number bytes
    buff.set(new Uint8Array([0x09]), command.length + serialBytes.length + 1);

    await this.writeToImpulse(buff, 'writeSerialNumberx');
    this.serialNumber = serialNumber;

    this.serialNumber$.next(serialNumber);
  }

  private async processPreload(data: Uint8Array) {
    if (data.length < 10) {
      this.logError('processPreload', 'Invalid message received');
    }
    /*
        17   01  07  02  00  00  00  00  00  04
        [17][01][07][02][00][00][00][00][00][04]
        CMD
        uint8_t soh;
        uint8 cmd;
        uint8_t level;
        uint8_t empty[5];
        uint8_t eot;
        */
    let soh = data[1];
    let cmd = data[2];
    let level = data[3];

    let preLoad = PreloadLevelType.none;

    if (level == 1) preLoad = PreloadLevelType.amber;
    else if (level == 2) preLoad = PreloadLevelType.green;

    this.preload$.next(preLoad);

    await this.requestPicSettings();
  }

  private isBleUnavailableError(error: unknown): boolean {
    const code = (error as { code?: string } | null)?.code;
    if (code === 'UNAVAILABLE') {
      return true;
    }

    const message =
      error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return message.includes('Web Bluetooth API not available');
  }

  private log(message: string, ...optionalParams: any[]) {
    const debugMessage = {
      message: message, // Main message
      optionalParams, // Additional optional parameters
    };
    this.debug$.next(debugMessage); // Emit combined debug data
  }

  private logError(message: string, ...optionalParams: any[]) {
    const debugMessage = {
      message: message, // Main message
      optionalParams, // Additional optional parameters
    };
    this.error$.next(debugMessage);
  }

  public static getConnected(): boolean {
    return IQConnect.connected;
  }

  public usesModernForceMapping(): boolean {
    // Legacy protocol (or not yet detected) uses legacy force levels; modern protocol uses modern levels.
    return this.legacyProtocol === false;
  }

  public static resolveForceValue(level: number, useModernMapping: boolean): number | null {
    const map = useModernMapping
      ? IQConnect.MODERN_FORCE_VALUE_BY_LEVEL
      : IQConnect.LEGACY_FORCE_VALUE_BY_LEVEL;
    return map[level] ?? null;
  }

  public static resolveForceLevel(forceValue: number, useModernMapping: boolean): number {
    const entries = useModernMapping
      ? IQConnect.MODERN_FORCE_VALUE_BY_LEVEL
      : IQConnect.LEGACY_FORCE_VALUE_BY_LEVEL;

    for (const [level, value] of Object.entries(entries)) {
      if (value === forceValue) {
        return Number(level);
      }
    }

    return 0;
  }

  public getForceValueForLevel(level: number): number | null {
    return IQConnect.resolveForceValue(level, this.usesModernForceMapping());
  }

  public getForceLevelFromValue(forceValue: number): number {
    return IQConnect.resolveForceLevel(forceValue, this.usesModernForceMapping());
  }

  /**
   * Get Received BLE Data (Observable)
   */
  // Expose Observables for Components to Subscribe to

  onFrequency(): Observable<IFrequency[] | null> {
    return this.frequency$.asObservable();
  }
  onForce(): Observable<number | null> {
    return this.force$.asObservable();
  }
  onPreload(): Observable<PreloadLevelType> {
    return this.preload$.asObservable();
  }
  onConnected(): Observable<string | null> {
    return this.connected$.asObservable();
  }
  onDisconnected(): Observable<void> {
    return this.disconnected$.asObservable();
  }
  onSerialNumber(): Observable<string | null> {
    return this.serialNumber$.asObservable();
  }
  onDisplayBoardSerialNumber(): Observable<string | null> {
    return this.displayBoardSn$.asObservable();
  }
  onMainBoardSerialNumber(): Observable<string | null> {
    return this.mainBoardSn$.asObservable();
  }
  onDisplayBoardFirmwareVersion(): Observable<string | null> {
    return this.displayBoardFw$.asObservable();
  }
  onMainBoardFirmwareVersion(): Observable<string | null> {
    return this.mainBoardFw$.asObservable();
  }
  onInitialized(): Observable<boolean> {
    return this.initialized$.asObservable();
  }
  onDebug(): Observable<IDebugMessage | null> {
    return this.debug$.asObservable();
  }
  onError(): Observable<IDebugMessage | null> {
    return this.error$.asObservable();
  }
  onScanning(): Observable<boolean> {
    return this.onScanning$.asObservable();
  }
  onTriggerState(): Observable<boolean> {
    return this.triggerState$.asObservable();
  }
  onDoubleTrigger(): Observable<void> {
    return this.onDoubleTrigger$.asObservable();
  }

  onSessionLog(): Observable<ISessionLog | null> {
    return this.onSessionLog$.asObservable();
  }
  onTreatment(): Observable<Treatment | null> {
    return this.treatment$.asObservable();
  }
  onIsConnected(): Observable<boolean> {
    return this.isConnected$.asObservable();
  }
}

class Treatment implements ITreatment {
  id: string | null = null;
  cumalitiveDose: number | null;
  dose: number | null;
  started: Date;
  ended: Date | null;
  frequency: IFrequency[];
  success: boolean;
  forceValue: number | null;
  serialNumber: string;
  deviceId: string;
  bodyPartId?: number;
  maskId?: number;
  mobilityMode: MobilityCalc;
  partFindings: string | null;
  recalled: boolean | null;
  bodyPart?: BodyPart | null | undefined;
  treatmentSide: string | null;
  readingData: string | null;

  constructor(started: Date, serialNumber: string, deviceId: string) {
    this.mobilityMode = MobilityCalc.THREE_HIGH_THREE_LOW;
    this.cumalitiveDose = null;
    this.dose = null;
    this.started = started;
    this.ended = null;
    this.frequency = [];
    this.success = false;
    this.forceValue = null;
    this.serialNumber = serialNumber;
    this.deviceId = deviceId;
    this.treatmentSide = null;
    this.recalled = false;
    this.partFindings = '';
    this.readingData = null;
  }

  public setForce(forceIndex: number, useModernMapping: boolean = false) {
    this.forceValue = IQConnect.resolveForceValue(forceIndex, useModernMapping);
  }

  public setDose(dose: number) {
    this.dose = Number.isFinite(dose) ? dose : 0;
  }

  public setReadingData(data: Uint8Array) {
    const triggers = this.getUShortAt(data, 3) + 1; //3,4
    const dose = this.getUShortAt(data, 5); //5,6
    const thrusts = this.getUShortAt(data, 7); //7,8

    const arr = this.dataToString(data).match(/.{1,2}/g);
    if (arr) {
      let readingData = {
        rawData: arr.join(' '),
        triggers: '[' + arr[3] + ' ' + arr[4] + ']' + ' => ' + triggers,
        dose: '[' + arr[5] + ' ' + arr[6] + ']' + ' => ' + dose,
        thrusts: '[' + arr[7] + ' ' + arr[8] + ']' + ' => ' + thrusts,
      };

      this.readingData = JSON.stringify(readingData);
    }
  }

  public addFrequency(eventTime: Date, frequencyIndex: number): IFrequency[] {
    const index = this.frequency.length + 1;
    let itm: IFrequency = {
      eventTime: eventTime,
      index: index,
      value: 12 + frequencyIndex * -0.5,
      frequencyIndex: frequencyIndex,
    };

    this.frequency.push(itm);
    return this.frequency;
  }

  get mobilityPre(): number | null {
    return this.frequency.length > 0
      ? MobilityCalcMethods.getPre(this.mobilityMode, this.frequency)
      : null;
  }

  get mobilityPost(): number | null {
    return this.frequency.length > 0
      ? MobilityCalcMethods.getPost(this.mobilityMode, this.frequency)
      : null;
  }

  get mobilityPrePercentage(): number | null {
    return this.mapValueToPercentage(this.mobilityPre);
  }

  get mobilityPostPercentage(): number | null {
    return this.mapValueToPercentage(this.mobilityPost);
  }

  get mobilityChangeAbsolute(): number | null {
    if (
      this.frequency.length >=
        MobilityCalcMethods.getMinFrequenciesToCalcMobility(this.mobilityMode) &&
      this.mobilityPost !== null &&
      this.mobilityPre !== null
    ) {
      return parseFloat((this.mobilityPost - this.mobilityPre).toFixed(2));
    }
    return null;
  }

  get mobilityChangePercent(): number | null {
    if (
      this.frequency.length >=
        MobilityCalcMethods.getMinFrequenciesToCalcMobility(this.mobilityMode) &&
      this.mobilityPost !== null &&
      this.mobilityPre !== null
    ) {
      return parseFloat(Math.round((this.mobilityPost / this.mobilityPre - 1) * 100).toFixed(2));
    }
    return null;
  }

  get duration(): number | null {
    if (this.started && this.ended) {
      return parseFloat(((this.ended.getTime() - this.started.getTime()) / 1000).toFixed(2));
    }
    return null;
  }

  mapValueToPercentage(x: number | null): number | null {
    if (!x) return x;

    const minInput = 4;
    const maxInput = 12;
    const minOutput = 1;
    const maxOutput = 100;

    // Clamp x to stay within the input range
    const clampedX = Math.max(minInput, Math.min(maxInput, x));
    const percentage =
      ((clampedX - minInput) / (maxInput - minInput)) * (maxOutput - minOutput) + minOutput;
    return parseFloat(Math.round(percentage).toFixed(2));
  }

  private getUShortAt(data: Uint8Array, idx: number): number {
    return (((data[idx + 1] & 0xff) << 8) | (data[idx] & 0xff)) >>> 0; // Ensure unsigned value
  }

  private dataToString(data: Uint8Array): string {
    return Array.from(data)
      .map((byte) => `${byte.toString(16).toUpperCase().padStart(2, '0')}`)
      .join('');
  }
}

export enum MobilityCalc {
  FIRST_LAST = 0,
  MIN_MAX = 1,
  FIRST_MAX = 2,
  MIN_LAST = 3,
  FIRST_AVG = 4,
  THREE_HIGH_THREE_LOW = 5,
  FIRST_AVG_OF_LAST_THREE = 6,
}

export const MobilityCalcMethods = {
  getPre: (mode: MobilityCalc, frequencies: IFrequency[]): number | null => {
    if (frequencies.length < 1) return null;

    switch (mode) {
      case MobilityCalc.FIRST_LAST:
        return parseFloat(frequencies[0].value.toFixed(2));
      case MobilityCalc.MIN_MAX:
        return parseFloat(Math.min(...frequencies.map((f) => f.value)).toFixed(2));
      case MobilityCalc.FIRST_MAX:
        return frequencies.length >= 2 ? parseFloat(frequencies[0].value.toFixed(2)) : null;
      case MobilityCalc.MIN_LAST:
        return frequencies.length >= 2
          ? parseFloat(Math.min(...frequencies.slice(0, -1).map((f) => f.value)).toFixed(2))
          : null;
      case MobilityCalc.FIRST_AVG:
        return frequencies.length >= 2 ? parseFloat(frequencies[0].value.toFixed(2)) : null;
      case MobilityCalc.THREE_HIGH_THREE_LOW:
        if (frequencies.length >= 3) {
          // Get the three lowest values and calculate their average
          const sortedLow = [...frequencies.map((f) => f.value)].sort((a, b) => a - b).slice(0, 3);
          return parseFloat((sortedLow.reduce((sum, v) => sum + v, 0) / 3).toFixed(2));
        }
        return null;
      case MobilityCalc.FIRST_AVG_OF_LAST_THREE:
        return frequencies.length >= 3 ? parseFloat(frequencies[0].value.toFixed(2)) : null;
      default:
        return null;
    }
  },

  getPost: (mode: MobilityCalc, frequencies: IFrequency[]): number | null => {
    if (frequencies.length < 1) return null;

    switch (mode) {
      case MobilityCalc.FIRST_LAST:
        return parseFloat(frequencies[frequencies.length - 1].value.toFixed(2));
      case MobilityCalc.MIN_MAX:
        return parseFloat(Math.max(...frequencies.map((f) => f.value)).toFixed(2));
      case MobilityCalc.FIRST_MAX:
        return frequencies.length >= 2
          ? parseFloat(Math.max(...frequencies.slice(1).map((f) => f.value)).toFixed(2))
          : null;
      case MobilityCalc.MIN_LAST:
        return frequencies.length >= 2
          ? parseFloat(frequencies[frequencies.length - 1].value.toFixed(2))
          : null;
      case MobilityCalc.FIRST_AVG:
        return frequencies.length >= 2
          ? parseFloat(
              (
                frequencies.slice(1).reduce((sum, f) => sum + f.value, 0) /
                (frequencies.length - 1)
              ).toFixed(2),
            )
          : null;
      case MobilityCalc.THREE_HIGH_THREE_LOW:
        if (frequencies.length >= 3) {
          // Get the three highest values and calculate their average
          const sortedHigh = [...frequencies.map((f) => f.value)].sort((a, b) => b - a).slice(0, 3);
          return parseFloat((sortedHigh.reduce((sum, v) => sum + v, 0) / 3).toFixed(2));
        }
        return null;
      case MobilityCalc.FIRST_AVG_OF_LAST_THREE:
        return frequencies.length >= 3
          ? parseFloat((frequencies.slice(-3).reduce((sum, f) => sum + f.value, 0) / 3).toFixed(2))
          : null;
      default:
        return null;
    }
  },

  getMinFrequenciesToCalcMobility: (mode: MobilityCalc): number => {
    switch (mode) {
      case MobilityCalc.FIRST_LAST:
      case MobilityCalc.MIN_MAX:
        return 1;
      case MobilityCalc.FIRST_MAX:
      case MobilityCalc.MIN_LAST:
      case MobilityCalc.FIRST_AVG:
        return 2;
      case MobilityCalc.THREE_HIGH_THREE_LOW:
      case MobilityCalc.FIRST_AVG_OF_LAST_THREE:
        return 3;
      default:
        return 1;
    }
  },
};
